/**
 * Default page javascript. All other pages extend this one.
 */

// Polyfill
import arrayFindPolyfill from "./arrayFind";
import smoothscroll from "smoothscroll-polyfill";

// Libs
import whenDomReady from "when-dom-ready";
import "./lazysizes";
import AsyncAlpine from "async-alpine";
import Alpine from "alpinejs";
import mask from "@alpinejs/mask";
import alpineIntersect from "@alpinejs/intersect";
import * as DOMPurify from "dompurify";

import "../magics";

import siteConfig from "./siteConfig";
import "./sui/components";
import yupRanger from "./yupRanger";
import extend from "./extend";
import nav from "./nav";
import navSearch from "./navSearch";
import links from "./links";
import placeholder from "./form";
import carousel from "./carousel";
import "./stickyHeader";
import "./skipToMainContent";
import "./responsiveTables";
import sharePage from "./sharePage";
import user from "./user";
import gsa from "./gsa";
import storeListing from "./storeListing";
import browserHistory from "./browserHistory";
import { scrollToHash } from "./scrollTo";
import { addHtmlSupportClass } from "./isBrowserSupported";
import { trackEvent } from "./track";
import "./imageGallery";
import "./slider";
import "./slideCountry";
import "./testimonialSlider";
import "./animatedTextBlock";
import "./animatedTextSlider";
import "./comparator";
import "./background";
import "./cookiePolicy";

// CHC JS files/
import { removeUrlLastSlash, announcementFunc, anchorScrollToFunc, scrollToPos } from "./chc/helpers";
import carouselCHC from "./chc/carousel";
import noCardPeekCarousel from "./chc/noCardPeekCarousel";
import "./chc/searchAlgolia";
import "./chc/buttonAnchor";
import "./chc/buttonSticky";
import "./chc/stickyContentGrid";
import "./chc/header";
import "./chc/animatedBgOnScroll";
import "./parallax";
import "./quicklinksV2";
import "./accordion";
import "./accordions";
import "./heroVideo";
import "./typeform";
import lottieAnim from "./chc/lottieAnim";

import "../magics/chc/videoHelpers";

// Stores
import "../stores/sectionId";

// Styles
import "../../css/pages/BasePage.scss";

window.Alpine = Alpine;
window.purify = DOMPurify;


class BasePage {
    constructor(...opts) {
        extend(this, opts);
        this.init();
    }

    init() {
        const self = this;
        self.ready = false;
        addHtmlSupportClass();
        self.onBeforeReady();

        whenDomReady().then(function () {
            self.onReady();
        });

        window.addEventListener("load", function () {
            self.onLoad();
        });
        smoothscroll.polyfill();

        Alpine.plugin(AsyncAlpine);
        Alpine.plugin(mask);
        Alpine.plugin(yupRanger);
        Alpine.plugin(alpineIntersect);

        Alpine.start();
    }

    onBeforeReady() {
        extend(this, siteConfig);
        arrayFindPolyfill();
    }

    onReady() {
        const self = this;
        self.ready = true;
        const isEditMode = document.querySelector("body").classList.contains("mgnl-admin");

        if (isEditMode) {
            window.sessionStorage.setItem("siteThemeColors", (this.getResolvedCSSRulesByClasses([
                "text-white", "text-black", "text-primary1", "text-primary2", "text-primary3", "text-secondary1", "text-secondary2", "text-secondary3", "text-secondary4", "text-secondary5", "text-secondary6", "text-secondary7",  "text-secondary8", "text-tertiary1", "text-tertiary2", "text-tertiary3",  "text-tertiary4", "text-tertiary5", "text-tertiary6", "text-tertiary7", "text-tertiary8",
                "bg-white", "bg-black", "bg-primary1", "bg-primary2", "bg-primary3", "bg-secondary1", "bg-secondary2", "bg-secondary3", "bg-secondary4", "bg-secondary5", "bg-secondary6", "bg-secondary7", "bg-secondary8", "bg-tertiary1", "bg-tertiary2", "bg-tertiary3", "bg-tertiary4", "bg-tertiary5", "bg-tertiary6", "bg-tertiary7", "bg-tertiary8",
            ])));
        }   
        nav.init();
        navSearch.init();
        links.init();
        placeholder.init();
        carousel.init();

        sharePage.init();
        user.init();
        gsa.init();
        storeListing.init();
        browserHistory.init();
        // stickyHeader.init();

        this.pushPageView();
        // If page has error
        if (document.body.classList.contains("errorPage")) {
            this.pushPageErrorDatalayer();
        }

        // CHC Functions
        carouselCHC.init();
        noCardPeekCarousel.init();
        lottieAnim.init();

        removeUrlLastSlash();
        announcementFunc();
        anchorScrollToFunc();


        const windowHash = window.location.hash;
        if (windowHash) scrollToPos(windowHash);
    }

    onLoad() {
        document.getElementsByTagName("body")[0].classList.add("loaded");
        scrollToHash();
    }

    isReady() {
        var self = this;
        return self.ready === true;
    }

    pushPageView() {
        trackEvent({
            event: "page_view",
            brand: (window.dataLayer && window.dataLayer[0].brand) ?? "",
            brandCluster:  (window.dataLayer && window.dataLayer[0].brandCluster) ?? "",
            brandwebsitetype:  (window.dataLayer && window.dataLayer[0].brandwebsitetype) ?? "",
            context:  (window.dataLayer && window.dataLayer[0].context) ?? "",
            country:  (window.dataLayer && window.dataLayer[0].country) ?? "",
            event_type:  (window.dataLayer && window.dataLayer[0].event_type) ?? "",
            gbu_related:  (window.dataLayer && window.dataLayer[0].gbu_related) ?? "",
            language:  (window.dataLayer && window.dataLayer[0].language) ?? "",
            page_name:  (window.dataLayer && window.dataLayer[0].page_name) ?? "",
            page_subtype:  (window.dataLayer && window.dataLayer[0].page_subtype) ?? "",
            page_type:  (window.dataLayer && window.dataLayer[0].page_type) ?? "",
            platform:  (window.dataLayer && window.dataLayer[0].platform) ?? "",
            region:  (window.dataLayer && window.dataLayer[0].region) ?? "",
            siteType:  (window.dataLayer && window.dataLayer[0].siteType) ?? "",
            subBrandCluster:  (window.dataLayer && window.dataLayer[0].subBrandCluster) ?? "",
            target:  (window.dataLayer && window.dataLayer[0].target) ?? "",
            therapeutical_area:  (window.dataLayer && window.dataLayer[0].therapeutical_area) ?? "",
        });
    }

    pushPageErrorDatalayer() {
        trackEvent({
            event: "error_page",
            error_type: "Page Not Found",
            page_referrer: window.location.href, // Get Current Page URL
            interaction_type: "error_page",
        });
    }

    getResolvedCSSRulesByClasses(classNames) {
        const results = [];
        const rootStyles = getComputedStyle(document.documentElement);
      
        function resolveCSSVariable(value) {
          // Replace all var(--name[, fallback]) with resolved value or fallback
          return value.replace(/var\(\s*(--[^),\s]+)(?:,\s*([^)]+))?\s*\)/g, (match, varName, fallback) => {
            const resolved = rootStyles.getPropertyValue(varName).trim();
            return resolved || (fallback ? fallback.trim() : match); // fallback or keep as-is
          });
        }
      
        for (const sheet of document.styleSheets) {
          let rules;
          try {
            rules = sheet.cssRules || sheet.rules;
          } catch (e) {
            continue; // Skip unreadable stylesheets
          }
      
          for (const rule of rules) {
            if (!rule.selectorText || !rule.style) continue;
      
            for (const className of classNames) {
              const selector = `.${className}`;
              const selectors = rule.selectorText.split(',').map(s => s.trim());
      
              if (selectors.includes(selector)) {
                const resolvedLines = [];
      
                for (const prop of rule.style) {
                  let value = rule.style.getPropertyValue(prop).trim();
      
                  // Replace var(...) with resolved value
                  value = resolveCSSVariable(value);
      
                  resolvedLines.push(`  ${prop}: ${value};`);
                }
      
                results.push(`${selector} {\n${resolvedLines.join('\n')}\n}`);
              }
            }
          }
        }
      
        return results.length > 0 ? results.join('\n\n') : null;
      }
      

    noop() {}
}

export default BasePage;
